import { useCallback, useState } from 'react'
import { router } from '@inertiajs/react'
import { blockDomId } from '@arianna/reader-core'
import { MasteryPanel } from '@arianna/reader-ui/mastery'
import { buildTocAdapter, findSectionForSubParaId } from './adapters.js'
import { createQuizSession } from './quizApi.js'
import CourseLayout from './CourseLayout.js'
import { sectionUrl, quizSessionUrl } from './urls.js'
import type { PageComponent } from './inertiaPage.js'
import type { PadronanzaPageProps } from './types.js'

// ---------------------------------------------------------------------------
// Padronanza — server-computed mastery report (TASK-009 Phase 4), now its
// own Inertia page under CourseLayout (TASK-014) instead of a branch inside
// the old Reader.tsx monolith. No reading chrome here (see task doc
// "Reading chrome" definition — it lives only in Lettura.tsx).
// ---------------------------------------------------------------------------

const Padronanza: PageComponent<PadronanzaPageProps> = ({ shell, toc, mastery }) => {
  const { manualVersionId } = shell
  const tocAdapter = buildTocAdapter(toc)

  const [expandedChapterId, setExpandedChapterId] = useState<string | null>(null)
  const handleToggleChapter = useCallback((chapterId: string) => {
    setExpandedChapterId((prev) => (prev === chapterId ? null : chapterId))
  }, [])

  const handleReview = useCallback(
    (subParaId: string) => {
      const target = findSectionForSubParaId(tocAdapter, subParaId)
      if (target == null) return
      const anchor = target.id === subParaId ? '' : `#${blockDomId(subParaId)}`
      router.visit(`${sectionUrl(manualVersionId, target.slug)}${anchor}`)
    },
    [tocAdapter, manualVersionId],
  )

  // Starting practice now creates the session and navigates straight to its
  // own URL (TASK-014 — quiz is a real page, see pages/reader/QuizSession.tsx)
  // instead of opening the removed QuizOverlay in place.
  const handleStartWeakPractice = useCallback(
    (_count: number) => {
      void createQuizSession(manualVersionId, 'fragile').then((sessionId) =>
        router.visit(quizSessionUrl(manualVersionId, sessionId)),
      )
    },
    [manualVersionId],
  )

  const handleStartChapterPractice = useCallback(
    (chapterId: string) => {
      void createQuizSession(manualVersionId, 'chapter', chapterId).then((sessionId) =>
        router.visit(quizSessionUrl(manualVersionId, sessionId)),
      )
    },
    [manualVersionId],
  )

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <MasteryPanel
        report={mastery}
        loading={false}
        error={null}
        expandedChapterId={expandedChapterId}
        onToggleChapter={handleToggleChapter}
        onReview={handleReview}
        onStartWeakPractice={handleStartWeakPractice}
        onStartChapterPractice={handleStartChapterPractice}
        // TASK-014 dedup: CourseLayout's own header/tabs already provide
        // back-navigation + the manual title breadcrumb.
        showHeader={false}
      />
    </div>
  )
}

Padronanza.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default Padronanza
